import { supabase } from '../lib/supabase.js';
import { getPhotoCounts, uploadEventPhoto, uploadTechPhoto, uploadFiles } from '../lib/photos.js';
import { openPlacePhotos } from '../lib/place-photos.js';
import { openEventPhotos } from '../lib/event-photos.js';
import { openEventEditModal } from '../components/event-edit-modal.js';
import { isAdmin } from '../lib/auth.js';
import { calcLeadTime, calcDepartureTime, getStribroRoute } from '../utils/planovac.js';

const WEEKDAYS = ['neděle', 'pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota'];
const MONTHS = ['', 'ledna', 'února', 'března', 'dubna', 'května', 'června', 'července', 'srpna', 'září', 'října', 'listopadu', 'prosince'];

function fmtDate(d) {
    const dt = new Date(d);
    return `${dt.getDate()}. ${dt.getMonth() + 1}. ${dt.getFullYear()}`;
}
function fmtWeekday(d) {
    return WEEKDAYS[new Date(d).getDay()];
}
function fmtTime(t) {
    if (!t) return '';
    return t.slice(0, 5);
}
function fmtTimeRange(start, end) {
    return [fmtTime(start), fmtTime(end)].filter(Boolean).join(' – ');
}
function fmtDateRange(start, end) {
    if (!end || end === start) return fmtDate(start);
    const a = new Date(start), b = new Date(end);
    if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
        return `${a.getDate()}. – ${b.getDate()}. ${a.getMonth() + 1}. ${a.getFullYear()}`;
    }
    return `${fmtDate(start)} – ${fmtDate(end)}`;
}

function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function computeTimes(ev, settings) {
    const place = ev.place;
    if (!place || !ev.time_start) return {};
    const lead  = calcLeadTime(place, settings, ev.extra_reserve_min || 0);
    const ts    = ev.time_start.slice(0, 5);
    const route = getStribroRoute(place, settings);
    const depPilsen = calcDepartureTime(ts, lead.pilsenMin);
    const travelSP  = Number(settings.travel_time_stribro_pilsen) || 32;
    let dep_stribro, dep_stribro_van;
    if (route === 'via_pilsen') {
        dep_stribro     = calcDepartureTime(ts, lead.pilsenMin + travelSP);
        dep_stribro_van = depPilsen;
    } else if (route === 'via_stribro') {
        dep_stribro     = calcDepartureTime(ts, lead.pilsenMin - travelSP);
        dep_stribro_van = depPilsen;
    } else {
        dep_stribro     = calcDepartureTime(ts, lead.stribroMin);
        dep_stribro_van = null;
    }
    return {
        meeting_time:          calcDepartureTime(ts, lead.meetingMin),
        departure_pilsen:      depPilsen,
        departure_stribro:     dep_stribro,
        departure_stribro_van: dep_stribro_van,
        stribro_route:         route
    };
}

// Vrátí technické info místa jako HTML řádky (bez obalu) — '' když nic.
function techInfoHtml(place) {
    if (!place) return '';

    // 1. řádek: prostředí · zdroj · strana
    const line1Parts = [];
    if (place.environment) line1Parts.push(escapeHtml(place.environment.charAt(0).toUpperCase() + place.environment.slice(1)));
    const power = [place.power_type, place.power_side].filter(Boolean).join(' · ');
    if (power) line1Parts.push(escapeHtml(power));

    // 2. řádek: prodlužováky + ozvučení (inline, jen relevantní)
    const line2Parts = [];
    const cables = [];
    if (place.cable_400v && place.cable_400v !== 'žádný') cables.push(`400 V: ${escapeHtml(place.cable_400v)}`);
    if (place.cable_220v && place.cable_220v !== 'žádný') cables.push(`220 V: ${escapeHtml(place.cable_220v)}`);
    if (cables.length) line2Parts.push('Prodlužovák ' + cables.join(' · '));
    if (place.pa_variant) line2Parts.push(escapeHtml(place.pa_variant));

    // 3. řádek: TOP box + SUB fázování
    const line3Parts = [];
    if (place.top_box_angle) line3Parts.push(`TOP: ${escapeHtml(place.top_box_angle)}`);
    const subInfo = [];
    if (place.sub_placement) subInfo.push(escapeHtml(place.sub_placement));
    if (place.sub_delay_ms) subInfo.push(`${place.sub_delay_ms}ms`);
    if (place.sub_notes) subInfo.push(escapeHtml(place.sub_notes));
    if (subInfo.length) line3Parts.push(`SUB: ${subInfo.join(' · ')}`);

    // 4. řádek: stěhování (tagy) + koberec
    const tags = (place.moving_options || []).map(o => `<span class="tag">${escapeHtml(o)}</span>`).join(' ');
    const carpet = place.has_carpet ? '<span class="koberec-tag">Koberec na místě</span>' : '';

    const out = [];
    if (line1Parts.length) out.push(line1Parts.join('<span class="sep-dot">·</span>'));
    if (line2Parts.length) out.push(line2Parts.join('<span class="sep-dot">·</span>'));
    if (line3Parts.length) out.push(line3Parts.join('<span class="sep-dot">·</span>'));
    if (tags || carpet) out.push([tags, carpet].filter(Boolean).join(' '));
    return out.join('<br>');
}

function renderTechCell(place) {
    if (!place) return '<span style="color:var(--text-muted);">—</span>';
    const inner = techInfoHtml(place);
    if (!inner) return '<span style="color:var(--text-muted);">—</span>';
    return `<details class="tech-toggle"><summary>Technika</summary><div class="tech-body">${inner}</div></details>`;
}

function renderEventRow(ev, counts, settings) {
    const place = ev.place;
    const placeName = place?.name || '—';
    const times = computeTimes(ev, settings);
    const eventCount = counts?.events[ev.id] || 0;
    const techCount  = (place?.id && counts?.places[place.id]) || 0;
    const isPending = ev.status === 'pending';
    return `
        <tr${isPending ? ' class="row-pending"' : ''}>
            <td class="date-cell" data-label="Datum" data-event-id="${ev.id}" title="Admin: dvojklikem na datum upravíš akci">
                ${fmtDate(ev.event_date)}
                <span class="weekday">${fmtWeekday(ev.event_date)}</span>
                ${isPending
                    ? '<span class="badge badge-pending">V jednání</span>'
                    : '<span class="badge">Nadchází</span>'}
            </td>
            <td class="time-cell with-cta" data-label="Začátek / Konec">
                ${escapeHtml(fmtTimeRange(ev.time_start, ev.time_end))}
                <div style="margin-top:6px;">
                    <label class="photo-cta mini" title="Nahrát foto z akce">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14M5 12h14"/></svg>
                        Foto z akce <span class="cnt${eventCount === 0 ? ' empty' : ''}">${eventCount}</span>
                        <input type="file" accept="image/*,video/*,audio/*" multiple style="display:none;" data-upload-event="${ev.id}">
                    </label>
                </div>
            </td>
            <td class="place-cell" data-label="Místo">
                ${escapeHtml(placeName)}
                ${place?.id ? `
                <div class="place-photo-actions">
                    <label class="photo-cta tech mini" title="Nahrát technické foto místa">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14M5 12h14"/></svg>
                        Tech <span class="cnt${techCount === 0 ? ' empty' : ''}">${techCount}</span>
                        <input type="file" accept="image/*,video/*,audio/*" multiple style="display:none;" data-upload-tech="${place.id}">
                    </label>
                    <button class="gallery-link" data-place-gallery="${place.id}" data-place-name="${escapeHtml(placeName)}" title="Otevřít galerii místa">
                        <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="M21 15l-5-5L5 21"/></svg>
                        Galerie
                    </button>
                </div>` : ''}
            </td>
            <td class="time-cell" data-label="Sraz">${escapeHtml(fmtTime(times.meeting_time))}</td>
            <td class="time-cell" data-label="Odjezd Plzeň">${escapeHtml(fmtTime(times.departure_pilsen))}</td>
            <td class="time-cell" data-label="Odjezd Stříbro">
                ${times.stribro_route === 'via_pilsen'
                    ? `${escapeHtml(fmtTime(times.departure_stribro))}<br><span style="font-size:11px;color:var(--text-muted);">→ dodávka Plzeň ${escapeHtml(fmtTime(times.departure_stribro_van))}</span>`
                    : times.stribro_route === 'via_stribro'
                        ? `${escapeHtml(fmtTime(times.departure_stribro))}<br><span style="font-size:11px;color:var(--text-muted);">← dodávka Stříbro (Plzeň ${escapeHtml(fmtTime(times.departure_stribro_van))})</span>`
                        : escapeHtml(fmtTime(times.departure_stribro))}
            </td>
            <td data-label="Pořadatel">${escapeHtml(ev.organizer || '—')}${ev.organizer_phone ? `<br><span style="color:var(--text-muted);font-size:12px;">${escapeHtml(ev.organizer_phone)}</span>` : ''}</td>
            <td class="notes-cell" data-label="Poznámky">${escapeHtml(ev.notes || '')}</td>
            <td class="tech-cell" data-label="Technické">${renderTechCell(place)}</td>
        </tr>
    `;
}

function renderBlockRow(ev) {
    const name = ev.blocked_user?.name || 'Někdo';
    return `
        <tr class="blocked-row">
            <td class="date-cell" data-label="Datum">
                ${fmtDateRange(ev.event_date, ev.event_date_end)}
                <span class="weekday">${fmtWeekday(ev.event_date)}</span>
            </td>
            <td colspan="8" class="block-info" data-label="Blokace">
                <span class="badge blocked" style="margin-right:8px; vertical-align:middle;">Volno</span>
                <strong>${escapeHtml(name)}</strong> – <em>${escapeHtml(ev.block_reason || 'volno')}</em>
                <span class="block-reason">(termín blokován)</span>
            </td>
        </tr>
    `;
}

// ===== Mobilní kompaktní karta akce =====
function renderEventCard(ev, counts, settings) {
    const place = ev.place;
    const placeName = place?.name || '—';
    const eventCount = counts?.events[ev.id] || 0;
    const tech = techInfoHtml(place);
    const timeRange = fmtTimeRange(ev.time_start, ev.time_end);
    const times = computeTimes(ev, settings);

    const meet = [];
    if (times.meeting_time)     meet.push(`Sraz ${fmtTime(times.meeting_time)}`);
    if (times.departure_pilsen) meet.push(`Odjezd Plzeň ${fmtTime(times.departure_pilsen)}`);
    if (times.departure_stribro) {
        if (times.stribro_route === 'via_pilsen') {
            meet.push(`Odjezd Stříbro ${fmtTime(times.departure_stribro)} · přesed dodávka Plzeň ${fmtTime(times.departure_stribro_van)}`);
        } else if (times.stribro_route === 'via_stribro') {
            meet.push(`Dodávka Stříbro ${fmtTime(times.departure_stribro)} · odjezd Plzeň ${fmtTime(times.departure_stribro_van)}`);
        } else {
            meet.push(`Odjezd Stříbro ${fmtTime(times.departure_stribro)}`);
        }
    }

    const detail = [];
    if (meet.length) detail.push(`<div class="evc-line"><span class="evc-k">Sraz / odjezd</span>${meet.join(' · ')}</div>`);
    if (ev.notes)    detail.push(`<div class="evc-line"><span class="evc-k">Poznámky</span>${escapeHtml(ev.notes)}</div>`);
    if (tech)        detail.push(`<div class="evc-line"><span class="evc-k">Technika</span>${tech}</div>`);

    const isPending = ev.status === 'pending';
    return `
    <div class="evc${isPending ? ' evc-pending' : ''}">
        <div class="evc-head">
            <div class="evc-date" data-event-id="${ev.id}" title="Admin: dvojklikem upravíš akci">
                <span class="evc-d">${fmtDate(ev.event_date)}</span>
                <span class="evc-wd">${fmtWeekday(ev.event_date)}${timeRange ? ` · ${escapeHtml(timeRange)}` : ''}</span>
                ${isPending ? '<span class="badge badge-pending" style="margin-top:2px;">V jednání</span>' : ''}
            </div>
            <div class="evc-place"><strong>${escapeHtml(placeName)}</strong></div>
        </div>
        <div class="evc-actions">
            <label class="photo-cta mini" title="Nahrát foto z akce">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14M5 12h14"/></svg>
                Foto z akce <span class="cnt${eventCount === 0 ? ' empty' : ''}">${eventCount}</span>
                <input type="file" accept="image/*,video/*,audio/*" multiple style="display:none;" data-upload-event="${ev.id}">
            </label>
            <button type="button" class="evc-detail-toggle" data-evc-toggle>Detail akce</button>
        </div>
        <div class="evc-detail" hidden>
            ${detail.join('')}
            <button class="btn btn-sm evc-gallery-btn" data-event-gallery="${ev.id}" data-event-label="${escapeHtml(fmtDate(ev.event_date) + ' · ' + placeName)}">
                📷 Fotogalerie akce
            </button>
        </div>
    </div>`;
}

function renderBlockCard(ev) {
    // jen křestní jméno, jednou, hned vedle data
    const firstName = (ev.blocked_user?.name || 'Někdo').trim().split(/\s+/)[0];
    return `
    <div class="evc evc-block">
        <div class="evc-head">
            <div class="evc-date">
                <span class="evc-d">${fmtDateRange(ev.event_date, ev.event_date_end)}</span>
                <span class="evc-wd">${fmtWeekday(ev.event_date)}</span>
            </div>
            <div class="evc-who">
                <span class="badge blocked">Volno</span>
                <strong>${escapeHtml(firstName)}</strong>
            </div>
        </div>
    </div>`;
}

export async function renderPrehled(container) {
    container.innerHTML = '<div class="state-banner">Načítám akce…</div>';

    const today = new Date().toISOString().slice(0, 10);

    const [eventsResult, { data: settingsRows }] = await Promise.all([
        supabase
            .from('events')
            .select(`
                *,
                place:places(*),
                blocked_user:profiles!events_blocked_for_fkey(id, name)
            `)
            .gte('event_date', today)
            .order('event_date', { ascending: true }),
        supabase.from('settings').select('key, value')
    ]);
    const { data, error } = eventsResult;
    const settings = Object.fromEntries((settingsRows || []).map(s => [s.key, s.value]));

    if (error) {
        container.innerHTML = `<div class="state-banner error">Chyba načtení: ${escapeHtml(error.message)}</div>`;
        console.error(error);
        return;
    }

    if (!data || data.length === 0) {
        container.innerHTML = `
            <div class="state-banner">
                Žádné nadcházející akce. Přidej první přes záložku <em>Administrace</em> nebo přímo do databáze.
            </div>
        `;
        return;
    }

    // Načti počty fotek
    const eventIds = data.filter(e => e.type !== 'block').map(e => e.id);
    const placeIds = [...new Set(data.map(e => e.place?.id).filter(Boolean))];
    const counts = await getPhotoCounts({ eventIds, placeIds });

    const rows = data.map((ev) => (ev.type === 'block' ? renderBlockRow(ev) : renderEventRow(ev, counts, settings))).join('');
    const cards = data.map((ev) => (ev.type === 'block' ? renderBlockCard(ev) : renderEventCard(ev, counts, settings))).join('');

    container.innerHTML = `
        <div class="filters">
            <input class="search-input" type="search" placeholder="Hledat akci, místo, pořadatele...">
            <select class="filter-select">
                <option>Všechny akce</option>
                <option>Nadcházející</option>
                <option>Volna / blokace</option>
            </select>
        </div>

        <div class="table-wrap">
            <table class="events-table">
                <thead>
                    <tr>
                        <th>Datum</th>
                        <th>Začátek / Konec</th>
                        <th>Místo konání</th>
                        <th>Sraz</th>
                        <th>Odjezd<br>Plzeň</th>
                        <th>Odjezd<br>Stříbro</th>
                        <th>Pořadatel</th>
                        <th>Poznámky</th>
                        <th>Technické parametry</th>
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
            </table>
        </div>

        <div class="prehled-cards">${cards}</div>

        <div class="footer-note">
            Načteno z Supabase. <strong>${data.length}</strong> nadcházejících záznamů.
            Klikni na <em>Foto z akce</em> nebo <em>Foto technika</em> pro nahrání fotek.
            Proběhlé akce najdeš v záložce <em>Historie</em>.
        </div>
    `;

    // Upload foto z akce (přímo z Přehledu)
    container.querySelectorAll('[data-upload-event]').forEach(input => {
        input.addEventListener('change', async () => {
            const id = input.dataset.uploadEvent;
            const files = input.files;
            if (!files?.length) return;
            const label = input.closest('.photo-cta');
            label.style.opacity = '0.6';
            const original = label.querySelector('.cnt').textContent;
            label.querySelector('.cnt').textContent = '…';
            const { ok, fail, errors } = await uploadFiles(uploadEventPhoto, id, files);
            if (fail) {
                const detail = errors.map(e => `• ${e.name}: ${e.message}`).join('\n');
                alert(`Nahráno: ${ok}\nNepodařilo se: ${fail}\n\n${detail}`);
            }
            renderPrehled(container);
        });
    });

    // Galerie tlačítko – otevře modal s tech fotkami místa
    container.querySelectorAll('[data-place-gallery]').forEach(btn => {
        btn.addEventListener('click', () => {
            const id = btn.dataset.placeGallery;
            const name = btn.dataset.placeName;
            openPlacePhotos(id, name);
        });
    });

    // Fotogalerie akce (z rozbaleného detailu karty)
    container.querySelectorAll('[data-event-gallery]').forEach(btn => {
        btn.addEventListener('click', () => {
            openEventPhotos(btn.dataset.eventGallery, btn.dataset.eventLabel);
        });
    });

    // Admin: dvojklik na datum akce otevře editaci
    if (await isAdmin()) {
        container.querySelectorAll('[data-event-id]').forEach((el) => {
            el.style.cursor = 'pointer';
            el.addEventListener('dblclick', () => {
                openEventEditModal(el.dataset.eventId, () => renderPrehled(container));
            });
        });
    }

    // Rozbalení detailu mobilní karty
    container.querySelectorAll('[data-evc-toggle]').forEach(btn => {
        btn.addEventListener('click', () => {
            const body = btn.closest('.evc')?.querySelector('.evc-detail');
            if (!body) return;
            const willOpen = body.hasAttribute('hidden');
            body.toggleAttribute('hidden', !willOpen);
            btn.classList.toggle('open', willOpen);
        });
    });

    // Upload technické foto místa
    container.querySelectorAll('[data-upload-tech]').forEach(input => {
        input.addEventListener('change', async () => {
            const id = input.dataset.uploadTech;
            const files = input.files;
            if (!files?.length) return;
            const label = input.closest('.photo-cta');
            label.style.opacity = '0.6';
            label.querySelector('.cnt').textContent = '…';
            const { ok, fail, errors } = await uploadFiles(uploadTechPhoto, id, files);
            if (fail) {
                const detail = errors.map(e => `• ${e.name}: ${e.message}`).join('\n');
                alert(`Nahráno: ${ok}\nNepodařilo se: ${fail}\n\n${detail}`);
            }
            renderPrehled(container);
        });
    });
}
